import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, ChevronRight, Activity, User, Settings,
  LogIn, LogOut, Edit, Trash2, Plus, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogs = () => {
  const { token } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: ''
  });
  const limit = 30;

  useEffect(() => {
    fetchLogs();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      
      const response = await axios.get(`${API}/admin/logs?${params}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return <LogIn className="w-4 h-4" />;
    if (action.includes('logout')) return <LogOut className="w-4 h-4" />;
    if (action.includes('create') || action.includes('add')) return <Plus className="w-4 h-4" />;
    if (action.includes('update') || action.includes('edit')) return <Edit className="w-4 h-4" />;
    if (action.includes('delete')) return <Trash2 className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('login')) return 'bg-green-500/20 text-green-400';
    if (action.includes('logout')) return 'bg-slate-500/20 text-slate-400';
    if (action.includes('create') || action.includes('add') || action.includes('approve')) return 'bg-blue-500/20 text-blue-400';
    if (action.includes('update') || action.includes('edit')) return 'bg-yellow-500/20 text-yellow-400';
    if (action.includes('delete') || action.includes('reject')) return 'bg-red-500/20 text-red-400';
    return 'bg-slate-500/20 text-slate-400';
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'user': return <User className="w-4 h-4" />;
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'settings': return <Settings className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-4">
          <select
            value={filters.action}
            onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(0); }}
            className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
          >
            <option value="">All Actions</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </select>
          <select
            value={filters.resource_type}
            onChange={(e) => { setFilters({ ...filters, resource_type: e.target.value }); setPage(0); }}
            className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
          >
            <option value="">All Resources</option>
            <option value="user">User</option>
            <option value="group">Group</option>
            <option value="contribution">Contribution</option>
            <option value="expense">Expense</option>
            <option value="settings">Settings</option>
            <option value="admin">Admin</option>
            <option value="auth">Auth</option>
          </select>
        </div>
        <span className="text-sm text-slate-400">{total} logs total</span>
      </div>

      {/* Logs List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
      >
        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : logs.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-400">
            No activity logs found
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {logs.map((log, index) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="px-6 py-4 hover:bg-slate-800/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{log.admin_name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300 capitalize">
                        {log.resource_type}
                      </span>
                    </div>
                    {log.resource_id && (
                      <p className="text-sm text-slate-400 mt-1">
                        Resource ID: <span className="font-mono text-xs">{log.resource_id}</span>
                      </p>
                    )}
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 p-2 rounded-lg bg-slate-800/50 text-xs">
                        <pre className="text-slate-400 whitespace-pre-wrap break-all">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm text-slate-400">
                      {new Date(log.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(log.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="border-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="border-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminLogs;
